import React, { useState, useEffect } from 'react';
import { 
  signInWithCredential,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { 
  getGoogleClientId, 
  loadGoogleIdentityServices, 
  initializeGoogleIdentityServices,
  isGoogleIdentityServicesAvailable 
} from '../config/googleIdentityServices';

const ModernGoogleAuth = ({ onSuccess, onError, darkMode = false }) => {
  const [loading, setLoading] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [showFallbackMessage, setShowFallbackMessage] = useState(false);

  // Load Google Identity Services script
  useEffect(() => {
    const initializeGoogleAuth = async () => {
      try {
        await loadGoogleIdentityServices();
        setIsScriptLoaded(true);
        
        // Initialize Google Identity Services
        const success = initializeGoogleIdentityServices(handleGoogleResponse, {
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: true,
        });
        
        if (!success) {
          console.warn('Failed to initialize Google Identity Services');
          setShowFallbackMessage(true);
        }
      } catch (error) {
        console.error('Error loading Google Identity Services:', error);
        setShowFallbackMessage(true);
      }
    };

    initializeGoogleAuth();
  }, []);

  const handleGoogleResponse = async (response) => {
    try {
      console.log('Modern Google Identity Services response received');
      setLoading(true);
      
      // Create Firebase credential from Google ID token
      const credential = GoogleAuthProvider.credential(response.credential);
      
      // Sign in with Firebase
      const result = await signInWithCredential(auth, credential);
      
      console.log('Modern Google authentication successful:', result.user.email);
      
      // Call onSuccess if provided
      if (onSuccess) {
        await onSuccess(result);
      }
      
    } catch (error) {
      console.error('Modern Google authentication error:', error);
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!isScriptLoaded || !isGoogleIdentityServicesAvailable()) {
      console.error('Google Identity Services not available');
      setShowFallbackMessage(true);
      if (onError) onError(new Error('Google authentication not available'));
      return;
    }

    setLoading(true);
    console.log('Starting modern Google authentication...');
    
    try {
      // Show the One Tap prompt
      window.google.accounts.id.prompt((notification) => {
        console.log('Google prompt notification:', notification);
        
        if (notification.isNotDisplayed()) {
          console.log('One Tap not displayed, showing fallback...');
          setLoading(false);
          // Show a helpful message to the user
          setShowFallbackMessage(true);
        } else if (notification.isDismissedMoment()) {
          console.log('One Tap dismissed by user');
          setLoading(false);
        } else if (notification.isSkippedMoment()) {
          console.log('One Tap skipped by user');
          setLoading(false);
        }
      });
    } catch (error) {
      console.error('Error showing Google prompt:', error);
      setLoading(false);
      setShowFallbackMessage(true);
      if (onError) onError(error);
    }
  };

  // Fallback to legacy method if modern method fails
  const handleFallbackGoogleLogin = async () => {
    try {
      setLoading(true);
      setShowFallbackMessage(false);
      
      // Use Firebase's signInWithPopup as fallback
      const { signInWithPopup } = await import('firebase/auth');
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      
      console.log('Fallback Google authentication successful:', result.user.email);
      
      if (onSuccess) {
        await onSuccess(result);
      }
      
    } catch (error) {
      console.error('Fallback Google authentication error:', error);
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Modern Google Sign-In Button */}
      <div className="relative">
        <button
          onClick={handleGoogleLogin}
          disabled={loading || !isScriptLoaded}
          className={`w-full font-semibold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center space-x-3 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
            darkMode 
              ? 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-200' 
              : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 shadow-sm'
          }`}
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span>Signing in...</span>
            </>
          ) : (
            <>
              {/* Modern Google logo */}
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </>
          )}
        </button>
        

        
        {/* Loading state overlay */}
        {!isScriptLoaded && (
          <div className={`absolute inset-0 flex items-center justify-center rounded-xl ${
            darkMode ? 'bg-gray-800/50' : 'bg-gray-100/50'
          }`}>
            <div className="text-sm text-gray-500">Loading Google Sign-In...</div>
          </div>
        )}
      </div>

      {/* Fallback message and button */}
      {showFallbackMessage && (
        <div className={`p-4 rounded-lg border ${
          darkMode 
            ? 'bg-yellow-900/20 border-yellow-800/30 text-yellow-200' 
            : 'bg-yellow-50 border-yellow-200 text-yellow-800'
        }`}>
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium">
                Modern sign-in not available
              </p>
              <p className="text-xs mt-1 opacity-90">
                Your browser may need to allow cookies for Google authentication. 
                Click below to try the alternative sign-in method.
              </p>
              <button
                onClick={handleFallbackGoogleLogin}
                disabled={loading}
                className={`mt-2 text-xs font-medium underline hover:no-underline transition-all duration-200 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Signing in...' : 'Try Alternative Sign-In'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernGoogleAuth;
